import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/utils';
import { useAccountAuth } from '@/app/hooks/useAccountAuth';
import { accountBillingApi } from '@/services/endpoints/account/billing';
import { ACCOUNT_BILLING_KEYS } from './queryKeys';
import {
  SUBSCRIPTION_LABEL,
  SUBSCRIPTION_TONE,
  StatusPill,
  daysUntil,
  formatDay,
  formatMoney,
} from './accountUi';

/**
 * The account instrument: a readout that stays visible in every section, so the
 * answer to "what is my account doing right now" is never more than a glance
 * away and never a page you have to navigate to. Sections below it are the
 * work; this strip is the constant.
 *
 * It is also where trouble surfaces first — a past-due subscription turns the
 * strip into the alert rather than hiding the fact one tab deep.
 */

function Readout({
  label,
  value,
  sub,
  className,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('min-w-0 px-5 py-3 first:pl-0 lg:border-l lg:border-border', className)}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 truncate text-[15px] font-semibold leading-tight text-foreground">
        {value}
      </p>
      {sub && <p className="mt-0.5 truncate text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function AccountInstrument() {
  const { isOwner, context } = useAccountAuth();

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ACCOUNT_BILLING_KEYS.dashboard,
    queryFn: accountBillingApi.getDashboard,
    // Billing is owner-only server-side; an admin would only collect a 403.
    enabled: isOwner,
    staleTime: 60_000,
  });

  const { data: plans } = useQuery({
    queryKey: ACCOUNT_BILLING_KEYS.plans,
    queryFn: () => accountBillingApi.getPlans(),
    enabled: isOwner,
    staleTime: 5 * 60_000,
  });

  const subscription = dashboard?.subscription ?? null;
  const currency = subscription?.currency ?? 'cad';

  const basePlanName = useMemo(
    () => subscription?.items?.find((item) => item.planType === 'base')?.planName ?? null,
    [subscription]
  );

  const seatCap = useMemo(() => {
    const baseItem = subscription?.items?.find((item) => item.planType === 'base');
    if (!baseItem || !plans) return null;
    const plan = plans.find((candidate) => candidate.id === baseItem.planId);
    return plan?.maxUsers && plan.maxUsers > 0 ? plan.maxUsers : null;
  }, [subscription, plans]);

  const nextCharge = dashboard?.upcomingInvoice?.amount ?? null;
  const nextChargeDate =
    dashboard?.upcomingInvoice?.dueDate ?? subscription?.nextBillingDate ?? null;

  const dunning = subscription?.dunning;
  const inTrouble =
    subscription?.status === 'past_due' || subscription?.status === 'suspended';
  const graceDays = daysUntil(dunning?.graceEndsAt ?? null);

  // An admin who is not the owner sees the organization they are in, and is
  // told plainly why the money figures are absent rather than shown zeros.
  if (!isOwner) {
    return (
      <div className="border-b border-border bg-muted/30">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-1 px-4 py-3 sm:px-6">
          <p className="text-sm font-semibold text-foreground">{context?.organization?.name}</p>
          <p className="text-sm text-muted-foreground">
            Signed in as {context?.role?.name ?? 'member'} — billing is visible to the
            organization owner.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'border-b border-border transition-colors duration-300',
        inTrouble ? 'bg-destructive/[0.06]' : 'bg-muted/30'
      )}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {inTrouble && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-3 text-sm text-destructive"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="font-semibold">
              {subscription?.status === 'suspended'
                ? 'Access is suspended. The last payment did not go through.'
                : 'The last payment did not go through.'}
            </span>
            <span className="opacity-90">
              {graceDays !== null && graceDays > 0
                ? `Update your card within ${graceDays} day${graceDays === 1 ? '' : 's'} to keep access.`
                : 'Update your card to restore billing.'}
            </span>
            <Link
              to="/account/billing"
              className="font-semibold underline underline-offset-4 hover:opacity-80"
            >
              Update payment method
            </Link>
          </motion.div>
        )}

        <div
          className={cn(
            'grid grid-cols-2 divide-y divide-border lg:flex lg:items-stretch lg:divide-y-0',
            inTrouble && 'pt-1'
          )}
        >
          <Readout
            label="Status"
            value={
              isLoading ? (
                <span className="inline-block h-4 w-20 animate-pulse rounded bg-muted align-middle" />
              ) : subscription ? (
                <StatusPill tone={SUBSCRIPTION_TONE[subscription.status]}>
                  {SUBSCRIPTION_LABEL[subscription.status]}
                </StatusPill>
              ) : (
                <StatusPill tone="neutral">No subscription</StatusPill>
              )
            }
            sub={context?.organization?.name}
            className="col-span-2 lg:col-span-1"
          />
          <Readout
            label="Plan"
            value={basePlanName ?? (isLoading ? '…' : 'None')}
            sub={
              subscription
                ? subscription.billingCycle === 'yearly'
                  ? 'Billed yearly'
                  : 'Billed monthly'
                : 'Choose a plan to get started'
            }
          />
          <Readout
            label="Seats"
            value={
              <span className="font-mono tabular-nums">
                {dashboard?.currentUsage?.users ?? '—'}
                {seatCap ? <span className="text-muted-foreground"> / {seatCap}</span> : null}
              </span>
            }
            sub={seatCap ? 'Active users on plan' : 'Active users'}
          />
          <Readout
            label="Next charge"
            value={
              <span className="font-mono tabular-nums">
                {nextCharge !== null
                  ? formatMoney(nextCharge, currency)
                  : subscription
                    ? formatMoney(
                        subscription.billingCycle === 'yearly'
                          ? subscription.totalYearly
                          : subscription.totalMonthly,
                        currency
                      )
                    : '—'}
              </span>
            }
            sub={nextChargeDate ? formatDay(nextChargeDate) : 'No scheduled charge'}
          />
        </div>
      </div>
    </div>
  );
}
